import React, { useEffect, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  Box,
  IconButton,
  MenuItem,
  Radio,
  TextField,
  Typography
} from '@mui/material'
import clsx from 'clsx'
import { RRule, SPANISH } from 'rrule'
import dayjs from '../../../utils/dayjsConfig'
import {
  POS_SCHEDULE_UI_TYPES,
  buildSchedulePayload,
  getScheduleSummaryContent,
  isOneTimeRecurrence
} from '../utils/posTaskSchedule'
import ScheduleSummaryAlert from './ScheduleSummaryAlert'

const recurrenceWeekDays = [
  { rrule: RRule.MO, value: 'Lunes', text: 'L' },
  { rrule: RRule.TU, value: 'Martes', text: 'M' },
  { rrule: RRule.WE, value: 'Miércoles', text: 'M' },
  { rrule: RRule.TH, value: 'Jueves', text: 'J' },
  { rrule: RRule.FR, value: 'Viernes', text: 'V' },
  { rrule: RRule.SA, value: 'Sábado', text: 'S' },
  { rrule: RRule.SU, value: 'Domingo', text: 'D' }
]

const recurrenceSelectionType = [
  { value: POS_SCHEDULE_UI_TYPES.DAILY, text: 'Todos los días' },
  { value: POS_SCHEDULE_UI_TYPES.WEEKLY, text: 'Semanal' },
  { value: POS_SCHEDULE_UI_TYPES.ONCE, text: 'Una sola vez' },
  { value: POS_SCHEDULE_UI_TYPES.ONCE_PER_PERIOD, text: 'Una vez por periodo' }
]

const stepperButtonSx = {
  width: 34,
  height: 34,
  border: '1px solid #d8dde7',
  borderRadius: '6px',
  backgroundColor: '#fff'
}

function TaskScheduleConfig({
  isEnabled,
  onRecurrenceChange,
  onScheduleChange,
  onStartChange,
  onEndChange,
  isMandatoryTask,
  onMandatoryConflict,
  onDismissMandatoryConflict
}) {
  const [recurrenceWeekDaysSelection, setRecurrenceWeekDaysSelection] = useState([])
  const [startTime, setStartTime] = useState('')
  const [recurrenceSelection, setRecurrenceSelection] = useState('')
  const [recurrenceInterval, setRecurrenceInterval] = useState(1)
  const [endTime, setEndTime] = useState('')
  const [undefinedEndTime, setUndefinedEndTime] = useState(true)

  const recurrenceRule = useMemo(() => {
    if (recurrenceSelection === POS_SCHEDULE_UI_TYPES.DAILY) {
      return new RRule({ freq: RRule.DAILY })
    }

    if (recurrenceSelection === POS_SCHEDULE_UI_TYPES.WEEKLY) {
      if (recurrenceWeekDaysSelection.length === 0) return null

      return new RRule({
        freq: RRule.WEEKLY,
        interval: recurrenceInterval,
        byweekday: recurrenceWeekDaysSelection.map((day) => day.rrule)
      })
    }

    return null
  }, [recurrenceInterval, recurrenceSelection, recurrenceWeekDaysSelection])

  const recurrenceRuleText = useMemo(
    () => recurrenceRule?.toText?.(SPANISH) || '',
    [recurrenceRule]
  )

  const summaryContent = useMemo(
    () =>
      getScheduleSummaryContent({
        recurrenceType: recurrenceSelection,
        recurrenceInterval,
        startDate: startTime,
        endDate: endTime,
        isUndefinedEndDate: undefinedEndTime,
        ruleText: recurrenceRuleText
      }),
    [endTime, recurrenceInterval, recurrenceRuleText, recurrenceSelection, startTime, undefinedEndTime]
  )

  useEffect(() => {
    onRecurrenceChange?.(recurrenceRule)

    if (!recurrenceSelection) {
      onScheduleChange?.(null)
      return
    }

    const schedulePayload = buildSchedulePayload({
      recurrenceType: recurrenceSelection,
      recurrenceInterval,
      rule: recurrenceRule,
      ruleText: recurrenceRuleText
    })

    onScheduleChange?.(schedulePayload)
  }, [
    onRecurrenceChange,
    onScheduleChange,
    recurrenceInterval,
    recurrenceRule,
    recurrenceRuleText,
    recurrenceSelection
  ])

  const clampInterval = (value) => {
    let nextValue = Number(value)
    if (Number.isNaN(nextValue)) nextValue = 1
    if (nextValue < 1) nextValue = 1
    if (nextValue > 99) nextValue = 99
    return nextValue
  }

  const handleRecurrenceTypeChange = (event) => {
    const nextRecurrence = event?.target?.value
    setRecurrenceSelection(nextRecurrence)

    if (isOneTimeRecurrence(nextRecurrence) && isMandatoryTask) {
      onMandatoryConflict?.()
    }
  }

  const handleKeyDown = (event) => {
    if (event.keyCode === 38) {
      setRecurrenceInterval((current) => clampInterval(current + 1))
    } else if (event.keyCode === 40) {
      setRecurrenceInterval((current) => clampInterval(current - 1))
    }
  }

  const clearRecurrenceSelection = () => {
    setRecurrenceSelection('')
    setRecurrenceWeekDaysSelection([])
    setRecurrenceInterval(1)
    onDismissMandatoryConflict?.()
  }

  const handleStartTimeChange = (event) => {
    const nextStartTime = event?.target?.value
    setStartTime(nextStartTime)
    onStartChange?.(nextStartTime)

    if (!nextStartTime) {
      clearRecurrenceSelection()
    }
  }

  const handleEndTimeChange = (event) => {
    const nextEndTime = event?.target?.value
    setEndTime(nextEndTime)
    onEndChange?.(nextEndTime)

    if (!nextEndTime && !undefinedEndTime) {
      clearRecurrenceSelection()
    }
  }

  const handleRangeChange = (nextUndefinedEndTime) => {
    setUndefinedEndTime(nextUndefinedEndTime)
    if (nextUndefinedEndTime) {
      setEndTime('')
      onEndChange?.(null)
      return
    }

    if (!endTime) {
      clearRecurrenceSelection()
    }
  }

  const handleToggleDay = (day) => {
    const index = recurrenceWeekDaysSelection.findIndex(
      (selectedDay) => selectedDay.value === day.value
    )

    if (index === -1) {
      setRecurrenceWeekDaysSelection([...recurrenceWeekDaysSelection, day])
      return
    }

    setRecurrenceWeekDaysSelection(
      recurrenceWeekDaysSelection.filter((selectedDay) => selectedDay.value !== day.value)
    )
  }

  const onChangeRecurrenceInterval = (event) => {
    setRecurrenceInterval(clampInterval(event?.target?.value))
  }

  const increaseRecurrenceInterval = () => {
    setRecurrenceInterval((current) => clampInterval(current + 1))
  }

  const decreaseRecurrenceInterval = () => {
    setRecurrenceInterval((current) => clampInterval(current - 1))
  }

  const openNativeDatePicker = (event) => {
    const input = event?.currentTarget
    if (!input || input.disabled) return

    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker()
      }
    } catch (_error) {
      input.focus()
    }
  }

  const hasRequiredDates = Boolean(startTime) && (undefinedEndTime || Boolean(endTime))
  const isRecurrenceFieldEnabled = isEnabled && hasRequiredDates
  const isEndDateDisabled = !isEnabled || undefinedEndTime

  return (
    <div
      className={clsx('task-schedule-config', {
        'task-schedule-config--locked': !isEnabled
      })}
    >
      <div className="task-form-section-dates">
        <div className="title-promos-form">
          <b className="bold-step">
            PASO 5: Escoja una fecha de inicio y/o fecha final para esta tarea
          </b>
        </div>
        <hr className="hr-task-form" />

        <Box className="task-schedule-config__step5-layout">
          <Box className="task-schedule-config__step5-row task-schedule-config__step5-row--dates">
            <TextField
              className="start-date task-schedule-config__date-field"
              disabled={!isEnabled}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: dayjs().format('YYYY-MM-DD'),
                onClick: openNativeDatePicker
              }}
              label="Fecha inicio"
              name="startTime"
              onChange={handleStartTimeChange}
              required
              size="small"
              type="date"
              value={startTime}
            />

            <TextField
              className="end-date task-schedule-config__date-field"
              disabled={isEndDateDisabled}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: startTime || dayjs().format('YYYY-MM-DD'),
                onClick: openNativeDatePicker
              }}
              label="Fecha final"
              name="endTime"
              onChange={handleEndTimeChange}
              required={!undefinedEndTime}
              size="small"
              type="date"
              value={undefinedEndTime ? '' : endTime}
            />
          </Box>

          <Box className="task-schedule-config__step5-row task-schedule-config__step5-row--range">
            <Box
              className={clsx('task-schedule-config__range-option', {
                'is-selected': undefinedEndTime,
                'is-disabled': !isEnabled
              })}
              component="label"
            >
              <Radio
                checked={undefinedEndTime}
                disabled={!isEnabled}
                name="taskRange"
                onChange={() => handleRangeChange(true)}
                size="small"
                value="undefined"
              />
              <Box className="task-schedule-config__range-copy">
                <Typography className="task-schedule-config__range-title">Tarea indefinida</Typography>
                <Typography className="task-schedule-config__range-subtitle">
                  Se mantiene activa sin fecha de cierre
                </Typography>
              </Box>
            </Box>

            <Box
              className={clsx('task-schedule-config__range-option', {
                'is-selected': !undefinedEndTime,
                'is-disabled': !isEnabled
              })}
              component="label"
            >
              <Radio
                checked={!undefinedEndTime}
                disabled={!isEnabled}
                name="taskRange"
                onChange={() => handleRangeChange(false)}
                size="small"
                value="defined"
              />
              <Box className="task-schedule-config__range-copy">
                <Typography className="task-schedule-config__range-title">
                  Con fecha de expiración
                </Typography>
                <Typography className="task-schedule-config__range-subtitle">
                  Deja de estar disponible en la fecha final
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>

      <div className="task-form-section task-schedule-config__section-51">
        <div className="title-promos-form">
          <p>
            <b className="bold-step">PASO 5.1: Establezca una recurrencia para la tarea</b>
          </p>
        </div>

        <div className="task-form-control-width">
          <TextField
            select
            fullWidth
            size="small"
            label="Recurrencia"
            value={recurrenceSelection}
            onChange={handleRecurrenceTypeChange}
            required
            name="recurrenceSelectionType"
            sx={{
              pointerEvents: !isRecurrenceFieldEnabled ? 'none' : 'auto',
              opacity: !isRecurrenceFieldEnabled ? 0.6 : 1,
              backgroundColor: !isRecurrenceFieldEnabled ? '#f0f0f0' : 'transparent',
              '& .MuiInputBase-input': {
                cursor: !isRecurrenceFieldEnabled ? 'not-allowed' : 'pointer'
              }
            }}
          >
            <MenuItem disabled value="">
              Escoja una opción
            </MenuItem>
            {recurrenceSelectionType.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {(recurrenceSelection === POS_SCHEDULE_UI_TYPES.WEEKLY ||
          recurrenceSelection === POS_SCHEDULE_UI_TYPES.ONCE_PER_PERIOD) && (
          <Box className="task-form-control-width mt-20">
            <Typography sx={{ alignItems: 'center', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              Se repite cada
              <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.75 }}>
                <IconButton
                  aria-label="Restar semana"
                  disabled={!isRecurrenceFieldEnabled || recurrenceInterval <= 1}
                  onClick={decreaseRecurrenceInterval}
                  sx={stepperButtonSx}
                  type="button"
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <TextField
                  disabled={!isRecurrenceFieldEnabled}
                  inputProps={{ min: 1, max: 99, style: { textAlign: 'center' } }}
                  onChange={onChangeRecurrenceInterval}
                  onKeyDown={handleKeyDown}
                  size="small"
                  sx={{ width: 72 }}
                  type="number"
                  value={recurrenceInterval}
                />
                <IconButton
                  aria-label="Sumar semana"
                  disabled={!isRecurrenceFieldEnabled || recurrenceInterval >= 99}
                  onClick={increaseRecurrenceInterval}
                  sx={stepperButtonSx}
                  type="button"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              semana{recurrenceInterval > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {recurrenceSelection === POS_SCHEDULE_UI_TYPES.WEEKLY && (
          <>
            <Box className="task-form-control-width mt-20">
              <p>Escoja los día(s)</p>
              <div className="days-btn-group">
                {recurrenceWeekDays.map((day) => (
                  <button
                    className={`days-btn ${
                      recurrenceWeekDaysSelection.some((selectedDay) => selectedDay.value === day.value)
                        ? 'active'
                        : ''
                    }`}
                    disabled={!isRecurrenceFieldEnabled}
                    key={day.value}
                    onClick={() => handleToggleDay(day)}
                    title={day.value}
                    type="button"
                    value={day.value}
                  >
                    {day.text}
                  </button>
                ))}
              </div>
              <div className="fake-validator-container">
                <input
                  className="input-fake-validator"
                  disabled={!isRecurrenceFieldEnabled}
                  readOnly
                  required
                  type="text"
                  value={recurrenceWeekDaysSelection.length > 0 ? 'selected' : ''}
                />
              </div>
            </Box>
          </>
        )}

        {summaryContent && <ScheduleSummaryAlert content={summaryContent} />}

        <div className="mb-50" />
      </div>
    </div>
  )
}

export default TaskScheduleConfig
